import { saveAs } from 'file-saver';

import type {
  TableCsvOptions,
  TableExportFormatter,
  TableExportTable,
  TableExportValue,
} from './table-export.model';
import { tableExportText } from './table-export-text.util';

const CSV_SEPARATOR = ';';
const CSV_LINE_BREAK = '\r\n';
/** Without the BOM, Excel reads the file as ANSI and breaks every accent. */
const CSV_BOM = '﻿';
/** What a spreadsheet reads as the start of a formula (CSV injection, OWASP). */
const FORMULA_START = /^[=+\-@\t\r]/;

function escapeCsvCell(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * A text cell that a spreadsheet would run as a formula (`=`, `+`, `-`, `@`, tab,
 * CR) gets a leading `'`, which makes it plain text. Numbers and dates are left
 * alone: they come from typed values, and a negative number has to stay one.
 */
function guardFormula(text: string): string {
  return FORMULA_START.test(text) ? `'${text}` : text;
}

function cellText(
  value: TableExportValue,
  text: string
): string {
  return typeof value === 'string' ? guardFormula(text) : text;
}

/**
 * CSV text of a table: UTF-8 BOM, `;` between cells, CRLF between lines, the
 * optional preamble and an empty line, then the header row and the data. A cell
 * holding `;`, `"` or a line break is quoted; a text cell that would start a
 * formula is neutralised.
 */
export function buildTableCsv(
  table: TableExportTable,
  formatter?: TableExportFormatter,
  options?: TableCsvOptions
): string {
  const preamble = (options?.preamble ?? []).map((cells) =>
    cells.map(guardFormula)
  );
  const header = table.columns.map((column) => guardFormula(column.header));
  const lines = table.rows.map((row) =>
    table.columns.map((column, index) =>
      cellText(row[index], tableExportText(row[index], column, formatter))
    )
  );
  const blocks = preamble.length
    ? [...preamble, [], header, ...lines]
    : [header, ...lines];
  return (
    CSV_BOM +
    blocks
      .map((cells) => cells.map(escapeCsvCell).join(CSV_SEPARATOR))
      .join(CSV_LINE_BREAK)
  );
}

/** Downloads `buildTableCsv` as `<fileBaseName>.csv`. */
export function exportTableCsv(
  fileBaseName: string,
  table: TableExportTable,
  formatter?: TableExportFormatter,
  options?: TableCsvOptions
): void {
  const blob = new Blob([buildTableCsv(table, formatter, options)], {
    type: 'text/csv;charset=utf-8',
  });
  saveAs(blob, `${fileBaseName}.csv`);
}
